// Authenticated layout. Bouncing to /login when no admin session is
// present.
//
// Shell shape: 240-px sidebar on the left + main column on the right
// (sticky topbar + scrolling page content). The sidebar uses
// .admin-shell-side from app.css; the topbar uses .admin-shell-top.
import {
  CatchBoundary,
  createFileRoute,
  Outlet,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import { useState } from "react";
import { CurrencyPresentationProvider } from "@/components/currency/CurrencyPresentationProvider";
import { Sidebar } from "@/components/layout/Sidebar";
import { TopBar } from "@/components/layout/TopBar";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { OVERLAY_ROOT_ID } from "@/components/ui/OverlayPortal";
import { fetchMyPermissions, keys } from "@/lib/queries";
import { fetchMeOrNull } from "@/lib/auth";

export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData({
      queryKey: keys.me(),
      queryFn: fetchMeOrNull,
      staleTime: 5_000,
    });
    if (!me) {
      throw redirect({
        to: "/login",
        search: { redirect: location.href },
      });
    }
    // The actor's roles + permissions, so the operator nav can render
    // with the right items on first paint. Awaited rather than
    // prefetched since SM-534, because the same payload says whether
    // this admin is holding an operator-issued temporary password: the
    // server refuses every route in this shell in that state, so
    // rendering it first would paint a nav over a page of 403s.
    //
    // A failed lookup still falls through to the shell — useMyPerms
    // degrades to "show all", and the gate is enforced server-side
    // regardless, so a transient failure must not strand an admin on
    // the change-password screen.
    const perms = await context.queryClient
      .ensureQueryData({
        queryKey: keys.myPerms(),
        queryFn: fetchMyPermissions,
        staleTime: 60_000,
      })
      .catch(() => null);
    if (perms?.must_change_password) {
      throw redirect({ to: "/change-password" });
    }
  },
  component: AppLayout,
});

function AppLayout() {
  // Below lg the sidebar collapses into a slide-in drawer triggered
  // by the topbar hamburger. State lives here so both shells (rail
  // + drawer) stay in sync.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Reset the in-shell error boundary whenever the route changes, so
  // an "Access denied" panel on /audit clears once the operator picks
  // a different section in the sidebar.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Currency presentation wraps the whole authenticated shell: an operator's
  // configured names, marks and colours have to reach every figure, not only
  // the pages that happen to fetch the registry themselves. It renders its
  // children immediately — the seed is a complete presentation — so this adds
  // no loading state to the shell.
  return (
    <CurrencyPresentationProvider>
      <div className="admin-shell">
        <Sidebar
          mobileOpen={mobileNavOpen}
          onMobileClose={() => setMobileNavOpen(false)}
        />
        <div className="admin-main">
          <TopBar onMenuClick={() => setMobileNavOpen(true)} />
          <main className="admin-main-content">
            <CatchBoundary
              getResetKey={() => pathname}
              errorComponent={({ error, reset }) => (
                <ErrorPanel error={error} reset={reset} />
              )}
            >
              <Outlet />
            </CatchBoundary>
          </main>
          {/* Portal target for viewport overlays (OverlayPortal). Must sit
              inside .admin-main so portalled dialogs keep the light --opx-*
              token scope, and outside the route tree so no space-y margin
              or transformed wrapper can displace their fixed scrims. */}
          <div id={OVERLAY_ROOT_ID} />
        </div>
      </div>
    </CurrencyPresentationProvider>
  );
}
